/**
 * WebSocket upload protocol to sync-go's /ws/upload endpoint.
 *
 * Wire format (text frames):
 *   client → server:
 *     1. JSON { "type": "init", "filename": "...", "size": 123, "sha256": "..." }
 *     2. Binary chunks (<= 1 MiB)
 *     3. JSON { "type": "eof" }
 *
 *   server → client:
 *     { "type": "progress", "bytes": n, "rows": m }
 *     { "type": "parsed",   "batch_id": "...", "rows": m, "elapsed_ms": n }
 *     { "type": "error",    "message": "..." }
 *
 * Analogy: shield generator — hand off one projectile at a time, wait for the
 * capacitor ACK before queuing the next. Keeps the pipe from vaporizing RAM
 * on ~350 MB files.
 */

export type WsEvent =
  | { type: "ready"; batch_id: string }
  | { type: "progress"; bytes: number; rows: number }
  | { type: "parsed"; batch_id: string; rows: number; elapsed_ms: number }
  | { type: "error"; message: string };

export interface UploadOptions {
  file: File;
  chunkSize?: number;
  onEvent?: (event: WsEvent) => void;
  onReady?: (batchId: string) => void;
  signal?: AbortSignal;
}

export async function uploadApFile({
  file,
  chunkSize = 1 << 20, // 1 MiB
  onEvent,
  onReady,
  signal,
}: UploadOptions): Promise<{ batchId: string; rows: number; elapsedMs: number }> {
  const url = new URL("/ws/upload", window.location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";

  const ws = new WebSocket(url.toString());
  ws.binaryType = "arraybuffer";

  return new Promise((resolve, reject) => {
    const abort = () => {
      ws.close(4000, "client abort");
      reject(new Error("Upload aborted"));
    };
    signal?.addEventListener("abort", abort, { once: true });

    ws.onerror = () => reject(new Error("WebSocket error"));
    ws.onclose = (ev) => {
      if (ev.code !== 1000 && !ev.wasClean) {
        reject(new Error(`WebSocket closed: ${ev.code} ${ev.reason}`));
      }
    };

    ws.onopen = async () => {
      try {
        ws.send(
          JSON.stringify({
            type: "init",
            filename: file.name,
            size: file.size,
          }),
        );

        let offset = 0;
        while (offset < file.size) {
          if (signal?.aborted) return;
          const end = Math.min(offset + chunkSize, file.size);
          const slice = file.slice(offset, end);
          const buf = await slice.arrayBuffer();
          // Back-pressure: if the buffer is full, yield a tick.
          while (ws.bufferedAmount > chunkSize * 4) {
            await new Promise((r) => setTimeout(r, 10));
          }
          ws.send(buf);
          offset = end;
        }
        ws.send(JSON.stringify({ type: "eof" }));
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    };

    ws.onmessage = (msg) => {
      if (typeof msg.data !== "string") return;
      try {
        const event = JSON.parse(msg.data) as WsEvent;
        onEvent?.(event);
        if (event.type === "ready") {
          onReady?.(event.batch_id);
        } else if (event.type === "parsed") {
          ws.close(1000, "done");
          signal?.removeEventListener("abort", abort);
          resolve({
            batchId: event.batch_id,
            rows: event.rows,
            elapsedMs: event.elapsed_ms,
          });
        } else if (event.type === "error") {
          ws.close(1011, event.message);
          signal?.removeEventListener("abort", abort);
          reject(new Error(event.message));
        }
      } catch (err) {
        // Non-JSON frames ignored.
        console.warn("ws: unparseable frame", err);
      }
    };
  });
}
